import type { RequestPayload } from '@inertiajs/core';
import { router } from '@inertiajs/react';
import { useCallback, useEffect, useRef, useState } from 'react';

export function useServerFilters<T extends Record<string, unknown>>(baseFilters: T, route: string) {
    const [loading, setLoading] = useState(false);
    // Use a ref to store the latest baseFilters to avoid recreating applyFilters on every render
    const baseFiltersRef = useRef(baseFilters);

    useEffect(() => {
        baseFiltersRef.current = baseFilters;
    }, [baseFilters]);

    const applyFilters = useCallback(
        (nextFilters: Partial<T>) => {
            const query = { ...baseFiltersRef.current, ...nextFilters };

            const cleanedQuery = Object.fromEntries(
                Object.entries(query).filter(([, value]) => {
                    if (value === undefined || value === null) {
                        return false;
                    }

                    if (typeof value === 'string') {
                        return value !== '';
                    }

                    if (Array.isArray(value)) {
                        const filtered = value
                            .map((entry) => (typeof entry === 'string' ? entry.trim() : entry))
                            .filter((entry) => entry !== '' && entry !== undefined && entry !== null);

                        return filtered.length > 0;
                    }

                    return true;
                }),
            ) as Record<string, unknown>;

            setLoading(true);
            router.get(route, cleanedQuery as RequestPayload, {
                preserveState: true,
                preserveScroll: true,
                onFinish: () => {
                    setLoading(false);
                },
            });
        },
        [route],
    );

    return { applyFilters, loading };
}
